import { useEffect, useRef, useState } from 'react';

/**
 * A compact multi-select for the register filter rows (Marty, 01/09/2026: "Can the
 * filters be multi select — eg Location").
 *
 * Deliberately a checkbox list in a popover rather than a native `<select multiple>`:
 * the native control needs ctrl-click to add a second value, gives no indication that
 * it is multi-select, and renders as a tall scrolling box that would wreck the filter
 * row. This shows "All" / the single value / "N selected" in the same footprint as the
 * plain `<select>` it replaces.
 *
 * Selection is a string[] and empty means "no filter", matching how the register's
 * other filters behave.
 */
export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  className = '',
}: {
  /** Accessible name, e.g. "Filter by status". */
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape — a filter popover left open covers the rows
  // underneath it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const summary = selected.length === 0
    ? 'All'
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} selected`;

  return (
    <div ref={box} className={`relative ${className}`}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full truncate rounded border border-border bg-white px-1.5 py-1 text-left text-xs font-normal normal-case tracking-normal text-darkGrey"
      >
        {summary}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-56 overflow-auto rounded border border-border bg-white p-1 text-xs font-normal normal-case tracking-normal shadow-lg">
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mb-1 block w-full rounded px-2 py-1 text-left font-semibold text-navy hover:bg-bg"
            >
              Clear ({selected.length})
            </button>
          )}
          {options.map((o) => (
            <label key={o.value} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-bg">
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
                aria-label={o.label}
              />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
